import { createAdminAsyncThunk } from '@/store/thunks/createAdminAsyncThunk';
import { isAPIError } from '@/store/utils/index';
import type { APIError } from '@/types/api.types';
import type { LoginDTO, LoginRequest, LoginResult, LoginReturn } from '@/types/dtos.types';
import { normalizeEmail } from '@/utils/strings/strings';

const HTTP_UNAUTHORIZED = 401;
/** Backend discriminator from login-init when an admin's credentials are force-reset. */
const CREDENTIALS_RESET_REQUIRED_CODE = 'CREDENTIALS_RESET_REQUIRED';

/**
 * True when login-init blocked the sign-in because the account is under a forced
 * credential reset. Prefers the machine-readable `code`; falls back to the 401
 * message until the backend discriminator is deployed everywhere.
 */
const isCredentialsResetRequiredError = (error: APIError): boolean => {
  if (error.code === CREDENTIALS_RESET_REQUIRED_CODE) return true;
  return (
    error.status === HTTP_UNAUTHORIZED &&
    typeof error.message === 'string' &&
    /password reset required/i.test(error.message)
  );
};

const mapCredentialVerificationResult = (result: LoginResult): LoginReturn => ({
  twoFactorToken: result.twoFactorToken,
  maskedEmail: result.maskedEmail,
  maskedPhone: result.maskedPhone,
});

export const loginThunk = createAdminAsyncThunk<LoginResult, LoginDTO>(
  'auth/login',
  async(credentials, { extra: { authService }, rejectWithValue }) => {
    const username = normalizeEmail(credentials.username);
    try {
      const request: LoginRequest = {
        username,
        password: credentials.password,
      };
      const result = await authService.login(request);
      const mappedResult = mapCredentialVerificationResult(result);
      return mappedResult;
    } catch (error) {
      if (isAPIError(error)) {
        if (isCredentialsResetRequiredError(error)) {
          return rejectWithValue({
            credentialsResetRequired: true,
            emailAddress: username,
          });
        }
        return rejectWithValue({ fieldErrors: error.message });
      }
      throw error;
    }
  }
);
